import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '../../../../shared/components/ui';
import { CheckCircle, XCircle, AlertCircle, Shield, Download, FileText } from 'lucide-react';
import { exportAlignedData } from '../../../../services/api/dataExport';

interface AlignmentValidationProps {
  consumption: any;
  prices: any;
  onValidationChange?: (isValid: boolean) => void;
}

interface ValidationResult {
  isValid: boolean;
  consumptionRecords: number;
  priceRecords: number;
  timestampAlignment: {
    aligned: boolean;
    misalignedCount: number;
    examples: string[];
  };
  formatCheck: {
    consumption: boolean;
    prices: boolean;
    issues: string[];
  };
  completeness: {
    hasFullYear: boolean;
    coverage: number;
    missingPeriods: string[];
  };
}

export const DataAlignmentValidation: React.FC<AlignmentValidationProps> = ({
  consumption,
  prices,
  onValidationChange
}) => {
  const { t } = useTranslation();
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [exportFormat, setExportFormat] = useState<'csv' | 'excel'>('csv');
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  // Memoize the data to prevent unnecessary re-renders
  const consumptionDataLength = consumption?.data?.length || 0;
  const priceDataLength = prices?.data?.length || 0;
  
  // Create stable references for comparison
  const consumptionKey = useMemo(() => {
    if (!consumption?.data) return '';
    return `${consumptionDataLength}-${consumption.data[0]?.timestamp || ''}`;
  }, [consumptionDataLength, consumption?.data]);
  
  const priceKey = useMemo(() => {
    if (!prices?.data) return '';
    return `${priceDataLength}-${prices.data[0]?.timestamp || ''}`;
  }, [priceDataLength, prices?.data]);

  // Move validateAlignment before the useEffect that uses it to avoid initialization error

  const validateAlignment = useCallback(() => {
    if (!consumption?.data || !prices?.data || 
        consumption.data.length === 0 || prices.data.length === 0) {
      const result: ValidationResult = {
        isValid: false,
        consumptionRecords: consumption?.totalRecords || consumption?.data?.length || 0,
        priceRecords: prices?.records || prices?.totalRecords || prices?.data?.length || 0,
        timestampAlignment: {
          aligned: false,
          misalignedCount: 0,
          examples: []
        },
        formatCheck: {
          consumption: false,
          prices: false,
          issues: ['Missing data']
        },
        completeness: {
          hasFullYear: false,
          coverage: 0,
          missingPeriods: []
        }
      };
      setValidation(result);
      onValidationChange?.(false);
      return;
    }


    // Create timestamp maps for quick lookup
    const consumptionMap = new Map();
    const priceMap = new Map();
    
    consumption.data.forEach((item: any) => {
      if (item.timestamp) {
        const timestamp = normalizeTimestamp(item.timestamp);
        consumptionMap.set(timestamp, item);
      }
    });

    prices.data.forEach((item: any) => {
      if (item.timestamp) {
        const timestamp = normalizeTimestamp(item.timestamp);
        priceMap.set(timestamp, item);
      }
    });

    // Get record counts
    const consumptionRecordCount = consumption.totalRecords || consumption.data.length;
    const priceRecordCount = prices.records || prices.totalRecords || prices.data.length;
    
    // Check timestamp alignment
    const allTimestamps = new Set([...consumptionMap.keys(), ...priceMap.keys()]);
    const misalignedTimestamps: string[] = [];
    
    // Only check alignment if both datasets are complete (8759+ records for full year)
    const bothComplete = consumptionRecordCount >= 8759 && priceRecordCount >= 8759;
    
    if (bothComplete) {
      allTimestamps.forEach(timestamp => {
        if (!consumptionMap.has(timestamp) || !priceMap.has(timestamp)) {
          misalignedTimestamps.push(timestamp);
        }
      });
    }

    // Check data formats
    const formatIssues: string[] = [];
    let consumptionFormatOk = true;
    let pricesFormatOk = true;

    // Check consumption data format
    // Backend now returns consistent field name: 'consumption' for both original and aligned data
    for (const item of consumption.data) {
      const consumptionValue = item.consumption;
      
      if (consumptionValue === undefined || consumptionValue === null) {
        consumptionFormatOk = false;
        formatIssues.push('Invalid consumption value found');
        break;
      }
      if (typeof consumptionValue !== 'number' || isNaN(consumptionValue)) {
        consumptionFormatOk = false;
        formatIssues.push('Invalid consumption value found');
        break;
      }
    }

    // Check price data format
    // Backend now returns consistent field name: 'priceUsd' for both original and aligned data
    for (const item of prices.data) {
      const priceValue = item.priceUsd;
      
      if (priceValue === undefined || priceValue === null) {
        pricesFormatOk = false;
        formatIssues.push('Invalid price value found');
        break;
      }
      if (typeof priceValue !== 'number' || isNaN(priceValue)) {
        pricesFormatOk = false;
        formatIssues.push('Invalid price value found');
        break;
      }
    }

    // Check completeness (full year = 8760 hours for non-leap year, 8784 for leap year)
    // Note: Due to DST in Chile, we may have 8783 unique timestamps (one hour repeats)
    const expectedHours = 8784; // 2024 is a leap year
    const expectedWithDST = 8783; // With DST, one hour repeats
    const actualHours = Math.min(consumptionMap.size, priceMap.size);
    const coverage = Math.min(100, (actualHours / expectedHours) * 100); // Cap at 100%
    
    // Find missing periods
    const missingPeriods = findMissingPeriods(allTimestamps);

    // Both datasets must have sufficient records for a full year (8759-8784 accounting for DST)
    const hasCorrectRecordCount = (
      (consumptionRecordCount >= 8759 && consumptionRecordCount <= 8784) && 
      (priceRecordCount >= 8759 && priceRecordCount <= 8784)
    );
    
    const result: ValidationResult = {
      isValid: consumptionFormatOk && 
               pricesFormatOk && 
               hasCorrectRecordCount && // Both must have 8759-8784 records
               (coverage >= 99 || consumptionRecordCount >= 8759), // Allow 1% tolerance or full year
      consumptionRecords: consumptionRecordCount,
      priceRecords: priceRecordCount,
      timestampAlignment: {
        aligned: misalignedTimestamps.length === 0 || 
                 (misalignedTimestamps.length <= 25 && consumptionRecordCount >= 8759), // Allow some misalignment for DST/aggregation
        misalignedCount: misalignedTimestamps.length,
        examples: misalignedTimestamps.slice(0, 5)
      },
      formatCheck: {
        consumption: consumptionFormatOk,
        prices: pricesFormatOk,
        issues: formatIssues
      },
      completeness: {
        hasFullYear: coverage >= 99.9 || 
                    (consumptionRecordCount >= 8759 && priceRecordCount >= 8759), // Full year data
        coverage: coverage,
        missingPeriods: consumptionRecordCount >= 8759 ? [] : missingPeriods // No gaps if we have full year data
      }
    };

    setValidation(result);
    
    // Always call onValidationChange to ensure parent knows the current state
    onValidationChange?.(result.isValid);
  }, [consumption, prices, onValidationChange]);

  // Debounced effect to call validateAlignment when data changes
  useEffect(() => {
    // Add a small delay to debounce rapid updates
    const timer = setTimeout(() => {
      validateAlignment();
    }, 300);
    
    return () => clearTimeout(timer);
  }, [consumptionKey, priceKey, validateAlignment]);

  const normalizeTimestamp = (timestamp: string): string => {
    // Convert various timestamp formats to a standard format
    const date = new Date(timestamp);
    // Remove milliseconds for comparison
    const isoString = date.toISOString();
    return isoString.split('.')[0] + 'Z';
  };

  const isLeapYear = (): boolean => {
    // 2024 is a leap year
    return true;
  };

  const findMissingPeriods = (timestamps: Set<string>): string[] => {
    if (timestamps.size === 0) return ['All data'];
    
    const sortedTimestamps = Array.from(timestamps).sort();
    const firstDate = new Date(sortedTimestamps[0]);
    const lastDate = new Date(sortedTimestamps[sortedTimestamps.length - 1]);
    
    const periods: string[] = [];
    
    // Check if we have complete 2024 data
    const hasCompleteYear = (
      firstDate.getFullYear() === 2024 && 
      firstDate.getMonth() === 0 && 
      firstDate.getDate() === 1 && 
      firstDate.getHours() === 0 &&
      lastDate.getFullYear() === 2024 && 
      lastDate.getMonth() === 11 && 
      lastDate.getDate() === 31 && 
      lastDate.getHours() === 23
    );
    
    if (!hasCompleteYear) {
      // Only show meaningful missing periods
      if (firstDate.getFullYear() === 2024 && (firstDate.getMonth() > 0 || firstDate.getDate() > 1)) {
        periods.push(`Missing start of 2024`);
      }
      if (lastDate.getFullYear() === 2024 && (lastDate.getMonth() < 11 || (lastDate.getMonth() === 11 && lastDate.getDate() < 31))) {
        periods.push(`Missing end of 2024`);
      }
    }
    
    // Check for gaps in the middle (simplified check)
    // Skip gap detection if we have sufficient timestamps for a full year (8759+)
    if (timestamps.size < 8759) {
      let lastTime = new Date(sortedTimestamps[0]).getTime();
      for (let i = 1; i < sortedTimestamps.length; i++) {
        const currentTime = new Date(sortedTimestamps[i]).getTime();
        const hoursDiff = (currentTime - lastTime) / (1000 * 60 * 60);
        
        // DST can cause apparent 2-hour gaps at transition
        if (hoursDiff > 2.5) { // More than 2.5 hours gap (accounting for DST)
          const gapStart = new Date(lastTime);
          const gapEnd = new Date(currentTime);
          periods.push(`Gap: ${gapStart.toLocaleDateString()} to ${gapEnd.toLocaleDateString()}`);
        }
        
        lastTime = currentTime;
      }
    }
    
    return periods.slice(0, 3); // Return max 3 periods for display
  };

  const handleExport = async () => {
    setIsExporting(true);
    setExportError(null);
    
    try {
      await exportAlignedData({
        format: exportFormat,
        projectId: undefined // Will use current session project
      });
      
    } catch (error) {
      console.error('Export failed:', error);
      setExportError(error instanceof Error ? error.message : 'Export failed');
    } finally {
      setIsExporting(false);
    }
  };

  if (!validation) {
    return null;
  }

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-lg font-semibold text-swiss-charcoal flex items-center gap-2">
          <Shield className="w-5 h-5 text-blue-500" />
          {t('dataAlignment.validation.title')}
        </h4>
        {validation.isValid ? (
          <CheckCircle className="w-6 h-6 text-green-500" />
        ) : (
          <XCircle className="w-6 h-6 text-red-500" />
        )}
      </div>

      <div className="space-y-4">
        {/* Overall Status */}
        <div className={`p-4 rounded-lg ${validation.isValid ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
          <div className="flex items-center gap-2">
            {validation.isValid ? (
              <>
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="font-medium text-green-800">{t('dataAlignment.validation.readyForOptimization')}</span>
              </>
            ) : (
              <>
                <AlertCircle className="w-5 h-5 text-red-600" />
                <span className="font-medium text-red-800">{t('dataAlignment.validation.issuesDetected')}</span>
              </>
            )}
          </div>
        </div>

        {/* Detailed Checks */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Timestamp Alignment */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-swiss-charcoal">{t('dataAlignment.validation.timestampAlignment')}</span>
              {validation.timestampAlignment.aligned ? (
                <CheckCircle className="w-4 h-4 text-green-500" />
              ) : (
                <XCircle className="w-4 h-4 text-red-500" />
              )}
            </div>
            <div className="text-xs text-swiss-stone">
              {validation.timestampAlignment.aligned ? (
                <span className="text-green-600">{t('dataAlignment.validation.timestampsMatch')}</span>
              ) : (
                <>
                  <span className="text-red-600">{t('dataAlignment.validation.misalignedTimestamps', { count: validation.timestampAlignment.misalignedCount })}</span>
                  {validation.timestampAlignment.examples.length > 0 && (
                    <div className="mt-1 text-xs">
                      <div className="text-red-500 font-semibold">{t('dataAlignment.validation.criticalAlignment')}</div>
                      <div>{t('dataAlignment.validation.examples')}: {validation.timestampAlignment.examples.slice(0, 2).join(', ')}</div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Format Check */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-swiss-charcoal">{t('dataAlignment.validation.dataFormat')}</span>
              {validation.formatCheck.consumption && validation.formatCheck.prices ? (
                <CheckCircle className="w-4 h-4 text-green-500" />
              ) : (
                <XCircle className="w-4 h-4 text-red-500" />
              )}
            </div>
            <div className="text-xs text-swiss-stone">
              {validation.formatCheck.consumption && validation.formatCheck.prices ? (
                <span className="text-green-600">{t('dataAlignment.validation.allFormatsValid')}</span>
              ) : (
                <>
                  {!validation.formatCheck.consumption && (
                    <div className="text-red-600">{t('dataAlignment.validation.consumptionFormatInvalid')}</div>
                  )}
                  {!validation.formatCheck.prices && (
                    <div className="text-red-600">{t('dataAlignment.validation.priceFormatInvalid')}</div>
                  )}
                  {validation.formatCheck.issues.map((issue, idx) => (
                    <div key={idx} className="text-red-600 text-xs">{issue}</div>
                  ))}
                </>
              )}
            </div>
          </div>

          {/* Completeness */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-swiss-charcoal">{t('dataAlignment.validation.yearCompleteness')}</span>
              {validation.completeness.hasFullYear ? (
                <CheckCircle className="w-4 h-4 text-green-500" />
              ) : (
                <XCircle className="w-4 h-4 text-red-500" />
              )}
            </div>
            <div className="text-xs text-swiss-stone">
              <div className={validation.completeness.hasFullYear ? 'text-green-600' : 'text-red-600'}>
                {t('dataAlignment.validation.coverage', { coverage: validation.completeness.coverage.toFixed(1) })}
              </div>
              {validation.completeness.missingPeriods.length > 0 && (
                <div className="mt-1 text-xs text-red-600">
                  {t('dataAlignment.validation.missing')}: {validation.completeness.missingPeriods.join(', ')}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Record Counts */}
        <div className="border-t pt-4">
          <div className="flex justify-between text-sm text-swiss-stone">
            <span>{t('dataAlignment.validation.consumptionRecords')}: {validation.consumptionRecords}</span>
            <span>{t('dataAlignment.validation.priceRecords')}: {validation.priceRecords}</span>
          </div>
          {(validation.consumptionRecords < 8783 || validation.priceRecords < 8783) && (
            <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5" />
                <div className="text-amber-800">
                  <div className="font-medium mb-1">{t('dataAlignment.validation.fullYearRequired')}</div>
                  <div className="text-xs">
                    {t('dataAlignment.validation.fullYearDescription')}
                    {validation.consumptionRecords < 8783 && (
                      <div>• {t('dataAlignment.validation.consumptionNeeds', { count: 8783 - validation.consumptionRecords })}</div>
                    )}
                    {validation.priceRecords < 8783 && (
                      <div>• {t('dataAlignment.validation.pricesNeed', { count: 8783 - validation.priceRecords })}</div>
                    )}
                    <div className="mt-1">{t('dataAlignment.validation.useCompletionMethods')}</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Export Section */}
        {validation.isValid && (
          <div className="border-t pt-4 mt-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-500" />
                <span className="text-sm font-medium text-swiss-charcoal">{t('dataAlignment.validation.exportAlignedData')}</span>
              </div>

              <div className="flex items-center gap-3">
                <select
                  value={exportFormat}
                  onChange={(e) => setExportFormat(e.target.value as 'csv' | 'excel')}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={isExporting}
                >
                  <option value="csv">{t('dataAlignment.validation.csvFormat')}</option>
                  <option value="excel">{t('dataAlignment.validation.excelFormat')}</option>
                </select>

                <button
                  onClick={handleExport}
                  disabled={isExporting}
                  className="flex items-center gap-2 px-4 py-1.5 text-sm font-medium text-white bg-blue-500 rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Download className="w-4 h-4" />
                  {isExporting ? t('dataAlignment.validation.exporting') : t('dataAlignment.validation.export')}
                </button>
              </div>
            </div>

            {exportError && (
              <div className="mt-2 p-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded">
                {exportError}
              </div>
            )}

            <div className="mt-2 text-xs text-swiss-stone">
              {exportFormat === 'csv'
                ? t('dataAlignment.validation.csvDescription')
                : t('dataAlignment.validation.excelDescription')}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};